import { Checkbox, Divider, Typography } from '@trackman/web-shared-components';

import { ReactSortable } from 'react-sortablejs';
import { ReactComponent as SixDotsIcon } from 'svg/SixDots.svg';
import { VisibleStatDto } from 'types/CodegenReportTypes';
import { useTranslation } from 'react-i18next';

type Props = {
  label: string;
  draggableCheckboxes: VisibleStatDto[];
  setDraggableCheckboxes: (draggableCheckboxes: VisibleStatDto[]) => void;
  disabled?: boolean;
  disabledKeys?: string[];
  useShortLabels?: boolean;
};

const VisibleStatCheckbox = ({
  option,
  useShortLabel,
  disabled,
  onChange,
}: {
  option: VisibleStatDto;
  useShortLabel: boolean;
  disabled?: boolean;
  onChange?: () => void;
}) => {
  const { t } = useTranslation();

  const label = t(option.key);
  const shortLabel = useShortLabel && t('short.' + option.key);

  return (
    <Checkbox
      label={useShortLabel ? shortLabel : label}
      hint={useShortLabel && label !== shortLabel && label}
      checked={option.checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

const GenericDraggableCheckboxConfig = ({
  label,
  draggableCheckboxes,
  setDraggableCheckboxes,
  disabled = false,
  disabledKeys = [],
  useShortLabels = false,
}: Props) => {
  const { t } = useTranslation();

  const sortedCheckboxes = [...draggableCheckboxes].sort((a, b) => a.order - b.order);

  return (
    <>
      <Typography variant='label bold' component='div' color='#A0A0A0'>
        {t(label)}
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ReactSortable
          list={sortedCheckboxes.map((option) => ({ ...option, id: option.key }))}
          setList={(list) => {
            const newCheckboxes = draggableCheckboxes.map((option) => ({
              ...option,
              order: list.findIndex((item) => item.id === option.key),
            }));
            setDraggableCheckboxes(newCheckboxes);
          }}
          filter='.disabled-drag'
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {sortedCheckboxes.map((option) => {
            const isDisabled = disabled || disabledKeys.includes(option.key);

            return (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                key={option.key}
                className={isDisabled ? 'disabled-drag' : ''}
              >
                <div
                  style={{
                    cursor: isDisabled ? 'default' : 'grab',
                    height: 16,
                    color: isDisabled ? 'transparent' : '#A0A0A0',
                  }}
                >
                  <SixDotsIcon />
                </div>
                <VisibleStatCheckbox
                  option={option}
                  useShortLabel={useShortLabels}
                  disabled={isDisabled}
                  onChange={() => {
                    const copy = [...draggableCheckboxes];
                    copy.find((item) => item.key === option.key)!.checked = !option.checked;
                    setDraggableCheckboxes(copy);
                  }}
                />
              </div>
            );
          })}
        </ReactSortable>
      </div>
      <Divider />
    </>
  );
};

export default GenericDraggableCheckboxConfig;
